// The horizontal tab strip — the Authoring app's, not a new one.
//
// ★ WHERE IT CAME FROM. Omar, 2026-09-08: "Replace the current long page with
// horizontal tabs, following the design pattern used in the customer Settings
// page." That page is `apps/fe/src/surface-settings.jsx:81-105`, and every
// number here is lifted from it rather than chosen: `gap: 4`, a 1px bottom rule
// on the strip, `padding: 10px 14px` per tab, `marginBottom: -1` so the active
// tab's 2px underline sits ON the rule rather than above it, 14px labels, weight
// 650 when active and 500 when not, `--accent` under the active one.
//
// It exists as a component rather than as markup inside the customer page for
// the ordinary reason: the moment a second screen wants tabs, a copy would be a
// second set of rules about what a tab looks like, and the two would drift
// (`feedback_one_rule_one_place`). It is deliberately dumb — it owns no state,
// makes no decision about which tabs exist, and knows nothing about customers.
//
// ── Two things it does that the original does not, both accessibility ────────
//
// The Authoring version sets `role="tablist"` and `aria-selected` but no
// `role="tabpanel"` and no arrow-key handling, so a screen reader announces the
// buttons as tabs and then finds no panel, and the arrow keys do nothing. Both
// are cheap to get right at the point where the pattern is being extracted, and
// expensive to retrofit across two apps later.

import { useRef } from "react";

export interface TabSpec<Id extends string> {
  id: Id;
  label: string;
}

export function Tabs<Id extends string>({
  tabs,
  current,
  onSelect,
  idPrefix,
}: {
  tabs: readonly TabSpec<Id>[];
  current: Id;
  onSelect: (id: Id) => void;
  /** Namespaces the DOM ids, so two tab strips on one page cannot collide. */
  idPrefix: string;
}) {
  const strip = useRef<HTMLDivElement>(null);

  // ← → move between tabs, Home/End jump to the ends. This is what `role="tab"`
  // promises; without it the role is a claim the widget does not keep.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    let next: number | null = null;
    const at = tabs.findIndex((t) => t.id === current);
    if (delta !== 0) next = (at + delta + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    const target = tabs[next];
    if (!target) return;
    onSelect(target.id);
    strip.current
      ?.querySelector<HTMLButtonElement>(`#${idPrefix}-tab-${target.id}`)
      ?.focus();
  };

  // ⚠️ THE RULE AND THE SCROLLER ARE TWO ELEMENTS, and that is the whole point
  //    (Omar, 2026-09-15: "remove from the horizontal tal the vertical scroll
  //    bar on the right").
  //
  //    They used to be one. The strip carried `borderBottom` AND `overflowX:
  //    auto`, and each button carried `marginBottom: -1` so its underline sat ON
  //    the rule. A NEGATIVE bottom margin shrinks a child's outer height, so the
  //    flex line measured 1px shorter than the buttons' border boxes — the strip
  //    overflowed vertically by exactly 1px. And CSS does not allow one axis to
  //    scroll while the other stays `visible`: setting `overflow-x: auto`
  //    computes `overflow-y` to `auto` too. One pixel of overflow, one permanent
  //    vertical scrollbar down the right-hand side of the tab strip.
  //
  //    ★ NOT fixed with `overflow-y: hidden` on the same element, which is the
  //      obvious move and the wrong one: clipping happens at the padding box, so
  //      it would eat the 1px of underline the negative margin exists to place,
  //      leaving the active tab a 1px rule instead of 2px. The defect would look
  //      fixed and the design would be quietly wrong.
  //
  //    Instead the OUTER div owns the rule and the INNER one scrolls. The
  //    negative margin moves to the strip as a whole, so nothing inside it
  //    overflows, `overflow-y: hidden` clips nothing, and the active tab's
  //    underline still lands on the rule. Every number below is unchanged.
  return (
    <div style={{ marginTop: 20, borderBottom: "1px solid var(--border)" }}>
      <div
        ref={strip}
        role="tablist"
        onKeyDown={onKeyDown}
        style={{
          display: "flex",
          gap: 4,
          // The strip scrolls rather than wrapping: a wrapped second row of tabs
          // moves the content down by a line depending on the window width, which
          // is worse than a short sideways scroll on a narrow screen.
          overflowX: "auto",
          // ★ Load-bearing. Without it this computes to `auto` and the strip grows
          //   a vertical scrollbar the moment anything inside is a pixel tall.
          overflowY: "hidden",
          // Lifts the whole strip onto the rule above — the job each button's own
          // `marginBottom: -1` used to do, from a place where it cannot overflow.
          marginBottom: -1,
        }}
      >
        {tabs.map((t) => {
          const active = t.id === current;
          return (
            <button
              key={t.id}
              id={`${idPrefix}-tab-${t.id}`}
              role="tab"
              aria-selected={active}
              aria-controls={`${idPrefix}-panel-${t.id}`}
              // Only the active tab is in the tab order; the arrow keys reach the
              // others. Otherwise Tab walks through every tab before reaching the
              // panel's own content, which is the standard tablist mistake.
              tabIndex={active ? 0 : -1}
              className="focusable"
              onClick={() => onSelect(t.id)}
              style={{
                appearance: "none",
                background: "transparent",
                border: 0,
                borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                padding: "10px 14px",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: active ? 650 : 500,
                color: active ? "var(--text)" : "var(--text-muted)",
                cursor: "default",
                whiteSpace: "nowrap",
              }}
              onMouseOver={(e) => {
                if (!active) e.currentTarget.style.color = "var(--text)";
              }}
              onMouseOut={(e) => {
                if (!active) e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The panel a tab controls. Paired with `Tabs` so the ids cannot drift apart. */
export function TabPanel({
  id,
  idPrefix,
  children,
}: {
  id: string;
  idPrefix: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      tabIndex={0}
      style={{ paddingTop: 22 }}
    >
      {children}
    </div>
  );
}
